<template>
  <article :class="['p2p-card', 'p2p-post', { off: !post.selected, 'is-wide': wide }]"
           :style="{ '--p2p-visual-width': visualWidth, '--p2p-visual-cap': visualCap }">
    <div class="p2p-between p2p-post-head">
      <div class="p2p-row" style="gap:8px">
        <span class="p2p-chip is-accent">{{ formatLabel }}</span>
        <span class="p2p-faint">{{ angleLabel }}</span>
      </div>
      <div class="p2p-row" style="gap:4px">
        <label class="p2p-check" :title="post.selected ? 'Included in publishing' : 'Left out of publishing'">
          <input type="checkbox" :checked="post.selected" :disabled="readonly" @change="toggleSelected($event.target.checked)"> Include
        </label>
        <v-menu v-if="!readonly" offset-y left>
          <template #activator="{ on, attrs }">
            <button type="button" class="p2p-icon-btn" aria-label="More actions" v-bind="attrs" v-on="on"><v-icon small>mdi-dots-horizontal</v-icon></button>
          </template>
          <v-list dense>
            <v-list-item @click="copyCaption"><v-list-item-title>Copy caption</v-list-item-title></v-list-item>
            <v-list-item v-if="mainMedia" @click="downloadMedia"><v-list-item-title>Download {{ post.format === 'video' ? 'video' : 'image' }}</v-list-item-title></v-list-item>
            <v-list-item @click="$emit('delete', post)"><v-list-item-title class="p2p-bad">Delete post</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div :class="['p2p-post-main', { 'has-visual': post.format !== 'text' }]">
      <!-- Visual -->
      <div v-if="post.format !== 'text'" class="p2p-post-visual">
        <template v-if="post.visual_status === 'ready' && post.media.length">
          <MediaImg :key="current.url" :src="current.url" :poster="current.poster_url || ''" :kind="current.kind"
                    :ratio="ratioOf(current)" :alt="altText" />
          <div v-if="post.media.length > 1" class="p2p-slider">
            <button type="button" class="p2p-icon-btn" :disabled="slide === 0" aria-label="Previous slide" @click="slide--"><v-icon small>mdi-chevron-left</v-icon></button>
            <span class="p2p-faint p2p-mono">{{ slide + 1 }} / {{ post.media.length }}</span>
            <button type="button" class="p2p-icon-btn" :disabled="slide === post.media.length - 1" aria-label="Next slide" @click="slide++"><v-icon small>mdi-chevron-right</v-icon></button>
          </div>
        </template>
        <div v-else-if="post.visual_status === 'processing' || post.visual_status === 'pending'" class="p2p-post-wait">
          <div class="p2p-skeleton" :style="{ aspectRatio: post.format === 'video' ? '9 / 16' : shapeRatio, width: '100%' }"></div>
          <div class="p2p-post-wait-label"><span class="p2p-dot is-accent pulse"></span>{{ stopping ? 'Stopping…' : (post.visual_stage || waitingLabel) }}</div>
          <button v-if="!readonly && post.visual_status === 'processing' && post.visual_operation_id && !stopping" type="button"
                  class="p2p-btn sm ghost p2p-post-stop" :disabled="busy" @click="stopVisual">Stop</button>
        </div>
        <div v-else class="p2p-empty" style="padding:28px 16px">
          <div class="p2p-text">{{ visualProblem }}</div>
          <button v-if="!readonly" type="button" class="p2p-btn sm" :disabled="busy" @click="newVisual(null)">Try again</button>
        </div>
      </div>

      <div class="p2p-post-side">
        <!-- Words -->
        <div v-if="!editing" class="p2p-stack" style="gap:10px">
          <p class="p2p-text p2p-pre p2p-caption">{{ post.content.caption }}</p>
          <div v-if="post.content.hashtags.length" class="p2p-row" style="gap:6px">
            <span v-for="tag in post.content.hashtags" :key="tag" class="p2p-chip mono">#{{ tag }}</span>
          </div>
          <button v-if="post.content.thread.length" type="button" class="p2p-btn ghost sm" style="align-self:flex-start;padding:0 6px" @click="showThread = !showThread">
            {{ showThread ? 'Hide' : 'Show' }} the {{ post.content.thread.length }}-part thread version
          </button>
          <ol v-if="showThread" class="p2p-thread">
            <li v-for="(part, i) in post.content.thread" :key="i" class="p2p-text p2p-pre">{{ part }}</li>
          </ol>
        </div>
        <div v-else class="p2p-stack">
          <label class="p2p-label">Caption
            <textarea v-model="form.caption" class="p2p-textarea" rows="8"></textarea>
            <span class="p2p-faint">{{ form.caption.length }} characters</span>
          </label>
          <label v-if="post.content.image" class="p2p-label">Headline on the image
            <input v-model="form.headline" class="p2p-input" maxlength="80">
          </label>
          <template v-if="post.content.slides.length">
            <div v-for="(slide, i) in form.slides" :key="slide.source" class="p2p-card tight raised">
              <div class="p2p-between">
                <div class="p2p-kicker">Slide {{ i + 1 }} · {{ roleLabel(slide.role) }}</div>
                <div v-if="slide.role === 'point'" class="p2p-row" style="gap:2px">
                  <button type="button" class="p2p-icon-btn" :disabled="i <= 1" :aria-label="'Move slide ' + (i + 1) + ' up'" @click="moveSlide(i, -1)"><v-icon small>mdi-arrow-up</v-icon></button>
                  <button type="button" class="p2p-icon-btn" :disabled="i >= form.slides.length - 2" :aria-label="'Move slide ' + (i + 1) + ' down'" @click="moveSlide(i, 1)"><v-icon small>mdi-arrow-down</v-icon></button>
                  <button type="button" class="p2p-icon-btn" :disabled="form.slides.length <= 3" :aria-label="'Remove slide ' + (i + 1)" @click="form.slides.splice(i, 1)"><v-icon small>mdi-close</v-icon></button>
                </div>
              </div>
              <input v-model="slide.headline" class="p2p-input" maxlength="90" :aria-label="'Slide ' + (i + 1) + ' headline'">
              <textarea v-model="slide.body" class="p2p-textarea" rows="2" maxlength="260" style="min-height:64px" :aria-label="'Slide ' + (i + 1) + ' text'"></textarea>
            </div>
            <span class="p2p-faint">The cover stays first and the closing slide last. A carousel keeps at least 3 slides.</span>
          </template>
          <label v-if="post.content.video && post.content.video.style === 'ai'" class="p2p-label">Video description
            <textarea v-model="form.videoPrompt" class="p2p-textarea" rows="4" maxlength="1200"></textarea>
            <span class="p2p-faint">What the next AI clip shows. Your current clip stays until you choose Change video.</span>
          </label>
          <template v-else-if="post.content.video">
            <div class="p2p-kicker">Scenes</div>
            <input v-for="(scene, i) in form.scenes" :key="i" v-model="form.scenes[i]" class="p2p-input" maxlength="120" :aria-label="'Scene ' + (i + 1)">
          </template>
          <label class="p2p-label">Hashtags <span class="p2p-faint">separated by spaces</span>
            <input v-model="form.hashtags" class="p2p-input">
          </label>
          <div class="p2p-row">
            <button type="button" class="p2p-btn sm" :disabled="busy" @click="saveEdit">Save</button>
            <button type="button" class="p2p-btn ghost sm" @click="editing = false">Cancel</button>
            <span v-if="visualTextChanged" class="p2p-faint">The visual is redrawn with your words, free of charge.</span>
          </div>
        </div>

        <!-- Claims that need a decision -->
        <div v-for="item in pendingClaims" :key="item.index" class="p2p-alert">
          <v-icon small color="#fcd34d">mdi-alert-outline</v-icon>
          <div class="p2p-stack" style="gap:8px">
            <div><strong>Check this:</strong> {{ sentence(item.claim.text) }} It is not in the facts we read on your site.</div>
            <div v-if="!readonly" class="p2p-row">
              <button type="button" class="p2p-btn sm soft" :disabled="busy" @click="claimDecision(item.index, 'keep')">It's true, keep it</button>
              <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="claimDecision(item.index, 'remove')">Remove the claim</button>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div v-if="!readonly" class="p2p-row p2p-post-actions">
          <button type="button" class="p2p-btn sm soft" :disabled="busy || editing" @click="startEdit"><v-icon small>mdi-pencil-outline</v-icon>Edit</button>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <button type="button" class="p2p-btn sm soft" :disabled="busy || isRewriting" v-bind="attrs" v-on="on">
                <v-icon small>mdi-auto-fix</v-icon>{{ isRewriting ? 'Rewriting…' : 'Rewrite' }} <span class="p2p-faint">· 1 credit</span>
              </button>
            </template>
            <v-list dense>
              <v-subheader v-if="post.format !== 'text'">Rewrites the words; the {{ post.format === 'video' ? 'video' : 'picture' }} stays as it is.</v-subheader>
              <v-list-item v-for="option in rewrites" :key="option.key" @click="rewrite(option.key)"><v-list-item-title>{{ option.label }}</v-list-item-title></v-list-item>
              <v-list-item @click="customOpen = true"><v-list-item-title>Something else…</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
          <v-menu v-if="post.format !== 'text'" offset-y>
            <template #activator="{ on, attrs }">
              <button type="button" class="p2p-btn sm soft" :disabled="busy || post.visual_status === 'processing'" v-bind="attrs" v-on="on">
                <v-icon small>mdi-image-edit-outline</v-icon>{{ post.format === 'video' ? 'Change video' : 'Change image' }}
              </button>
            </template>
            <v-list dense>
              <template v-if="post.format === 'video'">
                <v-list-item @click="newVisual('motion')"><v-list-item-title>Motion video from your images · {{ prices.video.motion - 1 }} credits</v-list-item-title></v-list-item>
                <v-list-item :disabled="!aiVideo" @click="newVisual('ai')"><v-list-item-title>AI video · {{ prices.video.ai - 1 }} credits{{ aiVideo ? '' : ' (Builder and Growth)' }}</v-list-item-title></v-list-item>
                <template v-if="aiVideo && images.length">
                  <v-subheader>AI video starting from one of your images · {{ prices.video.ai - 1 }} credits</v-subheader>
                  <v-list-item v-for="image in images" :key="'v' + image.id" @click="newVisual('ai', image.id)">
                    <v-list-item-avatar tile size="34"><MediaImg :src="image.url" ratio="1" /></v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ image.alt || image.id }}</v-list-item-title>
                      <v-list-item-subtitle v-if="image.seen" class="p2p-image-seen">{{ image.seen }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </template>
              <template v-else>
                <v-subheader>Use one of your images · {{ post.format === 'carousel' ? carouselPrice : prices.new_visual.mine }} credits</v-subheader>
                <v-list-item v-for="image in images" :key="image.id" @click="newVisual('mine', image.id)">
                  <v-list-item-avatar tile size="34"><MediaImg :src="image.url" ratio="1" /></v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ image.alt || image.id }}</v-list-item-title>
                    <v-list-item-subtitle v-if="image.seen" class="p2p-image-seen">{{ image.seen }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="post.format === 'image'" @click="newVisual('ai')">
                  <v-list-item-title>Create an AI picture · {{ prices.new_visual.ai }} credits</v-list-item-title>
                </v-list-item>
                <v-subheader>Shape · free</v-subheader>
                <v-list-item v-for="shape in shapeOptions" :key="shape.key" :disabled="shape.key === currentShape || post.visual_status !== 'ready'" @click="changeShape(shape.key)">
                  <v-list-item-title>{{ shape.label }}{{ shape.key === currentShape ? ' (current)' : '' }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </div>

        <div v-if="networkSummary.length" class="p2p-row" style="gap:6px">
          <span v-for="item in networkSummary" :key="item.platform" class="p2p-row" style="gap:5px" :title="item.note">
            <NetBadge :platform="item.platform" />
            <span :class="['p2p-faint', { 'p2p-bad': !item.ok }]">{{ item.ok ? (item.warning || 'Ready') : item.note }}</span>
          </span>
        </div>
        <div v-if="error" class="p2p-alert bad">{{ error }}</div>
      </div>
    </div>

    <v-dialog v-model="customOpen" max-width="520">
      <div class="p2p-card raised">
        <h2 class="p2p-h2">Rewrite this post</h2>
        <label class="p2p-label">What should change?
          <textarea v-model="custom" class="p2p-textarea" rows="4" maxlength="600" placeholder="For example: mention the free first conversion earlier"></textarea>
        </label>
        <div class="p2p-row">
          <button type="button" class="p2p-btn" :disabled="custom.trim().length < 3" @click="rewrite('custom')">Rewrite · 1 credit</button>
          <button type="button" class="p2p-btn ghost" @click="customOpen = false">Cancel</button>
        </div>
      </div>
    </v-dialog>
  </article>
</template>

<script>
import MediaImg from './MediaImg.vue';
import NetBadge from './NetBadge.vue';
import { get, patch, post as send, download, message, stopJob } from '../api';
import { GOAL_LABELS } from '../goals';

const FORMATS = { text: 'Text', image: 'Image', carousel: 'Carousel', video: 'Video' };
const SHAPES = [{ key: 'portrait', label: 'Portrait 4:5', ratio: '4 / 5', carousel: true }, { key: 'square', label: 'Square 1:1', ratio: '1 / 1', carousel: true },
  { key: 'landscape', label: 'Landscape 1.91:1', ratio: '300 / 157', carousel: false }, { key: 'story', label: 'Story 9:16', ratio: '9 / 16', carousel: false }];

export default {
  name: 'PostCard',
  components: { MediaImg, NetBadge },
  props: {
    post: { type: Object, required: true },
    images: { type: Array, default: () => [] },
    platforms: { type: Array, default: () => [] },
    prices: { type: Object, default: () => ({ new_visual: { mine: '1', ai: '5' }, video: { motion: 8, ai: 61 }, carousel: {} }) },
    aiVideo: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    // Kinds of work running for this post right now (from the parent's polling).
    working: { type: Array, default: () => [] },
    failure: { type: String, default: '' },
    // Set by PostGrid when the card is wide enough to show its visual beside the words.
    wide: { type: Boolean, default: false }
  },
  data: () => ({ slide: 0, editing: false, busy: false, rewriting: false, stopping: false, showThread: false, error: '', customOpen: false, custom: '',
    form: { caption: '', headline: '', slides: [], scenes: [], videoPrompt: '', hashtags: '' },
    rewrites: [{ key: 'shorter', label: 'Shorter' }, { key: 'more_direct', label: 'More direct' }, { key: 'less_salesy', label: 'Less salesy' },
      { key: 'founder_voice', label: 'Founder voice' }, { key: 'more_visual', label: 'More visual' }, { key: 'fresh', label: 'A fresh take' }] }),
  computed: {
    formatLabel() {
      const base = FORMATS[this.post.format] || this.post.format;
      if (this.post.format === 'carousel') return base + ' · ' + this.post.content.slides.length + ' slides';
      if (this.post.format === 'video' && this.post.content.video) return base + ' · ' + (this.post.content.video.style === 'ai' ? 'AI' : 'motion');
      return base;
    },
    angleLabel() { return GOAL_LABELS[this.post.angle] || ''; },
    isRewriting() { return this.rewriting || this.working.indexOf('rewrite') >= 0; },
    current() { return this.post.media[Math.min(this.slide, this.post.media.length - 1)] || {}; },
    mainMedia() { return this.post.media[0] || null; },
    altText() { return this.post.content.image ? this.post.content.image.headline : this.post.content.hook; },
    carouselPrice() {
      // A post edited down to 4 or 6 slides pays the next tier up, as the server quotes it.
      const slides = this.post.content.slides.length;
      const tier = slides <= 3 ? '3' : slides <= 5 ? '5' : '7';
      return Number(this.prices.carousel[String(slides)] || this.prices.carousel[tier] || 12) - 1;
    },
    currentShape() { return this.post.content.shape || 'portrait'; },
    shapeOptions() { return SHAPES.filter(shape => this.post.format !== 'carousel' || shape.carousel); },
    shapeRatio() { const shape = SHAPES.find(item => item.key === this.currentShape); return shape ? shape.ratio : '4 / 5'; },
    visualRatio() {
      const item = this.current;
      if (item.kind === 'video' || (!item.url && this.post.format === 'video')) return 9 / 16;
      if (item.width && item.height) return item.width / item.height;
      return this.shapeRatio.split('/').map(Number).reduce((width, height) => width / height);
    },
    // Beside the words, a visual is about 440 pixels tall whatever its shape (wide pictures are capped in width).
    visualWidth() { return Math.round(Math.min(520, Math.max(240, 440 * this.visualRatio))) + 'px'; },
    // Above the words, a visual is at most about 520 pixels tall, so tall shapes do not stretch the card.
    visualCap() { return Math.round(Math.max(260, 520 * this.visualRatio)) + 'px'; },
    waitingLabel() { return this.post.format === 'video' ? 'Making your video' : 'Designing your visual'; },
    visualProblem() {
      if (this.post.visual_stopped) return 'You stopped this. Your credits were not used.';
      // The server's stored reason first: "needs credits" may really mean "needs a plan".
      if (this.post.visual_status === 'needs_credits') {
        return this.post.visual_error || 'This visual is waiting. Add credits and try again.';
      }
      return this.post.visual_error || 'The visual could not be created.';
    },
    pendingClaims() {
      return (this.post.content.claims || []).map((claim, index) => ({ claim, index })).filter(item => item.claim.status === 'unsupported');
    },
    networkSummary() {
      return this.platforms.map(platform => {
        const value = this.post.networks[platform] || { ok: false, reason: 'Not supported' };
        return { platform, ok: value.ok, note: value.reason || '', warning: (value.warnings || [])[0] || '' };
      });
    },
    visualTextChanged() {
      const content = this.post.content;
      if (content.image && this.form.headline !== content.image.headline) return true;
      if (this.form.slides.length !== content.slides.length || this.form.slides.some((slide, i) => slide.source !== i)) return true;
      return content.slides.some((slide, i) => this.form.slides[i] && (this.form.slides[i].headline !== slide.headline || this.form.slides[i].body !== slide.body));
    }
  },
  watch: {
    'post.media'() { if (this.slide >= this.post.media.length) this.slide = 0; },
    'post.visual_status'(value) { if (value !== 'processing') this.stopping = false; },
    working(value) { if (value.indexOf('rewrite') < 0) this.rewriting = false; },
    failure(value) { if (value) { this.error = value; this.rewriting = false; } }
  },
  methods: {
    // A claim as a sentence: one full stop, never two.
    sentence(text) { const value = (text || '').trim(); return /[.!?…"”]$/.test(value) ? value : value + '.'; },
    startEdit() {
      const content = this.post.content;
      this.form = { caption: content.caption, headline: content.image ? content.image.headline : '',
        slides: content.slides.map((slide, index) => ({ role: slide.role, headline: slide.headline, body: slide.body, source: index })),
        scenes: content.video ? content.video.scenes.slice() : [], videoPrompt: content.video ? content.video.prompt || '' : '',
        hashtags: content.hashtags.join(' ') };
      this.editing = true;
    },
    async update(payload) {
      this.busy = true;
      this.error = '';
      try {
        const updated = await patch('/posts/' + this.post.id, { version: this.post.version, ...payload });
        this.$emit('updated', updated);
        return updated;
      } catch (error) {
        this.error = message(error);
        return null;
      } finally {
        this.busy = false;
      }
    },
    async saveEdit() {
      const content = this.post.content;
      const payload = {};
      if (this.form.caption !== content.caption) payload.caption = this.form.caption;
      if (content.image && this.form.headline !== content.image.headline) payload.headline = this.form.headline;
      if (content.slides.length && this.visualTextChanged) {
        payload.slides = this.form.slides.map(slide => ({ headline: slide.headline, body: slide.body, source: slide.source }));
      }
      if (content.video && content.video.style === 'ai') {
        if (this.form.videoPrompt.trim() !== (content.video.prompt || '')) payload.video_prompt = this.form.videoPrompt.trim();
      } else if (content.video) {
        payload.scenes = this.form.scenes;
      }
      const tags = this.form.hashtags.split(/[\s,]+/).map(tag => tag.replace(/^#/, '')).filter(tag => tag.length >= 2);
      if (tags.join(' ') !== content.hashtags.join(' ')) payload.hashtags = tags;
      if (!Object.keys(payload).length) { this.editing = false; return; }
      if (await this.update(payload)) this.editing = false;
    },
    toggleSelected(value) { this.update({ selected: value }); },
    ratioOf(item) {
      if (item.kind === 'video') return '9 / 16';
      return item.width && item.height ? item.width + ' / ' + item.height : this.shapeRatio;
    },
    roleLabel(role) { return { cover: 'cover', point: 'point', closing: 'closing' }[role] || role; },
    moveSlide(index, delta) {
      const target = index + delta;
      if (target < 1 || target > this.form.slides.length - 2) return;
      const moved = this.form.slides.splice(index, 1)[0];
      this.form.slides.splice(target, 0, moved);
    },
    changeShape(shape) { this.update({ shape }); },
    claimDecision(index, decision) { this.update(decision === 'keep' ? { keep_claims: [index] } : { remove_claims: [index] }); },
    async rewrite(instruction) {
      this.customOpen = false;
      this.rewriting = true;
      this.error = '';
      try {
        const operation = await send('/posts/' + this.post.id + '/rewrite', { instruction, custom: instruction === 'custom' ? this.custom : '' });
        this.$emit('operation', operation);
        this.custom = '';
      } catch (error) {
        this.rewriting = false;
        this.error = message(error);
      }
    },
    async newVisual(mode, image) {
      this.busy = true;
      this.error = '';
      try {
        const payload = {};
        if (mode) payload.mode = mode;
        if (image) payload.source_image = image;
        const operation = await send('/posts/' + this.post.id + '/visual', payload);
        this.$emit('operation', operation);
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    // Never-started work is cancelled at once; running work stops at its next check (an AI video also stops at the provider).
    async stopVisual() {
      this.busy = true;
      this.error = '';
      try {
        const operation = await get('/operations/' + this.post.visual_operation_id);
        if (operation.job_id) await stopJob(operation.job_id);
        this.stopping = true;
        this.$emit('operation', operation);
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async copyCaption() {
      try { await navigator.clipboard.writeText(this.post.content.caption); this.$emit('notice', 'Caption copied'); } catch (error) { this.error = 'Copy is not available in this browser.'; }
    },
    downloadMedia() {
      const item = this.current.url ? this.current : this.mainMedia;
      const extension = item.kind === 'video' ? 'mp4' : 'png';
      download(item.url, 'post-' + this.post.id + '-' + (this.slide + 1) + '.' + extension).catch(() => { this.error = 'The download failed. Please try again.'; });
    }
  }
};
</script>

<style>
.p2p-post.off{opacity:.62}
/* Visual above the words; the actions sit at the bottom so a row of equal-height cards lines them up. */
.p2p-post-main,.p2p-post-side{display:flex;flex-direction:column;gap:14px;flex:1 1 auto;min-width:0}
.p2p-post-side>.p2p-post-actions{margin-top:auto}
/* A wide card puts its visual beside the words; text-only posts keep a readable line length. */
.p2p-post.is-wide .p2p-post-main.has-visual{display:grid;grid-template-columns:min(var(--p2p-visual-width,360px),45%) minmax(0,1fr);gap:24px;align-items:stretch}
.p2p-post.is-wide .p2p-post-visual{align-self:start}
.p2p-post.is-wide .p2p-post-main:not(.has-visual) .p2p-caption{max-width:72ch}
.p2p-post-visual{display:flex;flex-direction:column;gap:8px;width:100%;max-width:var(--p2p-visual-cap,100%);align-self:center}
.p2p-image-seen{white-space:normal!important;display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-width:360px;line-height:1.35!important}
.p2p-post-wait{position:relative}
.p2p-post-wait-label{position:absolute;left:12px;bottom:12px;display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;background:rgba(11,11,16,.85);font-size:12.5px;color:var(--p2p-body)}
.p2p-post-stop{position:absolute;right:12px;bottom:10px;background:rgba(11,11,16,.85)!important}
.p2p-slider{display:flex;align-items:center;justify-content:center;gap:10px}
.p2p-caption{max-height:340px;overflow:auto}
.p2p-thread{margin:0;padding-left:20px;display:flex;flex-direction:column;gap:8px}
.p2p-post-actions{padding-top:4px;border-top:1px solid var(--p2p-line)}
.p2p-bad{color:var(--p2p-bad-text)!important}
</style>
